import { useCallback, useEffect } from 'react';

import { ApolloQueryResult, gql, useApolloClient } from '@apollo/client';
import { IsoTimestamp, assertIsoTimestamp, startOfDay } from '@modernloop/shared/datetime';
import { useFeatureFlagNumber } from '@modernloop/shared/feature-flag';
import { concurrencyLimitFromFlag, createConcurrencyLimiter } from '@modernloop/shared/utils';
import { addDays, isBefore, parseISO } from 'date-fns';
import { cloneDeep } from 'lodash';

import {
  DateTimeRangeOutput,
  JobStageInterviewGroupInput,
  SelfScheduleZoomHost,
  SuggestedTimeRangesCountDocument,
  SuggestedTimeRangesCountInput,
  SuggestedTimeRangesCountQuery,
  SuggestedTimeRangesCountQueryVariables,
  SuggestedTimeRangesCountQuery_suggestedTimeRangesCount_SuggestedTimeRangesCountOutput_timeRangeCounts_TimeRangeCount_range_DateTimeRangeOutput,
  SuggestedTimeRangesRequestType,
} from 'src/generated/mloop-graphql';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const SuggestedTimeRangesCount = gql`
  query SuggestedTimeRangesCount($input: SuggestedTimeRangesCountInput!) {
    suggestedTimeRangesCount(input: $input) {
      timeRangeCounts {
        range {
          startAt
          endAt
        }
        count
      }
      employeeMissingZoomUserId
    }
  }
`;

// One limiter per limit value, shared across invocations so overlapping calendar loads cannot
// exceed the org's cap; FIFO keeps earliest-enqueued ranges first. If the flag changes
// mid-session, old in-flight tasks briefly finish on the previous limiter, which is harmless.
const suggestedTimesFetchLimiters = new Map<number, ReturnType<typeof createConcurrencyLimiter>>();

const getSuggestedTimesFetchLimiter = (limit: number) => {
  let limiter = suggestedTimesFetchLimiters.get(limit);
  if (!limiter) {
    limiter = createConcurrencyLimiter(limit);
    suggestedTimesFetchLimiters.set(limit, limiter);
  }
  return limiter;
};

export type SelfScheduleTimeRange = Pick<
  SuggestedTimeRangesCountQuery_suggestedTimeRangesCount_SuggestedTimeRangesCountOutput_timeRangeCounts_TimeRangeCount_range_DateTimeRangeOutput,
  'startAt' | 'endAt'
>;

export type TimeRangesByDayMap = { [day: IsoTimestamp]: SelfScheduleTimeRange[] };

type Input = {
  applicationId: string;
  onEmployeeMissingZoomUserId: (employeeIds: string[]) => void;
  onError: (errorMessage: string) => void;
  requestType: SuggestedTimeRangesRequestType;
  selfScheduleZoomHost?: SelfScheduleZoomHost;
  taskId?: string;
};

const getTimeRangeKey = (range: SelfScheduleTimeRange): string => `${range.startAt}|${range.endAt}`;

const useLoadSelfScheduleOptionsByTimeRange = (params: Input) => {
  const { applicationId, onEmployeeMissingZoomUserId, onError, requestType, selfScheduleZoomHost, taskId } = params;

  const apolloClient = useApolloClient();
  const suggestedTimesFetchConcurrency = useFeatureFlagNumber('org_suggested_times_fetch_concurrency');

  /**
   * Every time the hook mounts clear selfScheduleOptionsCount
   * This is needed to handle the below cases:
   *   - User updated the interview plan.
   *   - The modal was opened again after some time and by then the options count may have changed.
   */
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect intentionally performs a side effect after render; revisit before adding logic.
  useEffect(() => {
    apolloClient.cache.evict({ fieldName: 'suggestedTimeRangesCount' });
  }, [apolloClient]);

  const loadSelfScheduleOptionsByTimeRange = useCallback(
    async (
      timeRanges: DateTimeRangeOutput[],
      timezone: string,
      advanceNoticeHours: number | undefined,
      countByDayMap: { [day: IsoTimestamp]: number },
      // onCountByDayMapChanged helps to immediately update the data as soon as we have it and not wait for all weeks count to load
      onCountByDayMapChanged: (newCountByDayMap: { [day: IsoTimestamp]: number }) => void,
      timeRangesByDayMap: TimeRangesByDayMap,
      onTimeRangesByDayMapChanged: (newTimeRangesByDayMap: TimeRangesByDayMap) => void,
      jobStageId: string,
      shouldRespectLoadLimit: boolean,
      canScheduleOverAvailableKeywords: boolean,
      canScheduleOverRecruitingKeywords: boolean,
      canScheduleOverFreeTime: boolean,
      customJobStageId?: string,
      fetchPolicy?: 'network-only',
      customInterviewPlan?: JobStageInterviewGroupInput[] | undefined,
      shouldSkip?: boolean
    ) => {
      const tasks: Array<() => Promise<void>> = [];
      const countByDayMapClone = cloneDeep(countByDayMap);
      const timeRangesByDayMapClone = cloneDeep(timeRangesByDayMap);

      if (shouldSkip) {
        return;
      }

      // The concurrency pool starts tasks in array order, so earliest ranges must come first.
      [...timeRanges]
        .sort((first, second) => first.startAt.localeCompare(second.startAt))
        .forEach((timeRange) => {
          let start = parseISO(startOfDay(timeRange.startAt, timezone));
          const end = parseISO(timeRange.endAt);

          // Clear dates for which we are fetching counts.
          while (start.getTime() < end.getTime()) {
            const key = start.toISOString();
            delete countByDayMapClone[key];
            delete timeRangesByDayMapClone[key];
            start = addDays(start, 1);
          }

          const input: SuggestedTimeRangesCountInput = {
            taskId,
            applicationId,
            timeRange,
            advanceNoticeHours,
            validateZoomHost: selfScheduleZoomHost === SelfScheduleZoomHost.Interviewer,
            includeEmptyRanges: false,
            shouldRespectLoadLimit,
            canScheduleOverRecruitingKeywords,
            canScheduleOverAvailableKeywords,
            canScheduleOverFreeTime,
            requestType,
          };

          // If customInterviewPlan is provided, use it. Otherwise, use customJobStageId or jobStageId.
          // customPlan of [] is not a valid input.
          if (customInterviewPlan && customInterviewPlan.length > 0) {
            input.customInterviewPlan = customInterviewPlan;
          }

          const variables: SuggestedTimeRangesCountQueryVariables = {
            input,
          };

          tasks.push(() =>
            apolloClient
              .query({
                query: SuggestedTimeRangesCountDocument,
                variables,
                fetchPolicy,
                context: {
                  batch: false,
                },
              })
              .then((result: ApolloQueryResult<SuggestedTimeRangesCountQuery>) => {
                if (result.data.suggestedTimeRangesCount?.employeeMissingZoomUserId) {
                  onEmployeeMissingZoomUserId(result.data.suggestedTimeRangesCount?.employeeMissingZoomUserId);
                }

                if (!result.data.suggestedTimeRangesCount?.timeRangeCounts) {
                  return;
                }

                result.data.suggestedTimeRangesCount.timeRangeCounts.forEach((timeRangeCount) => {
                  if (!timeRangeCount?.range?.startAt) {
                    return;
                  }

                  const day = startOfDay(timeRangeCount.range.startAt, timezone);
                  countByDayMapClone[day] = (countByDayMapClone[day] || 0) + (timeRangeCount.count || 0);

                  if (!timeRangeCount.count || !timeRangeCount.range.endAt) {
                    return;
                  }

                  const range: SelfScheduleTimeRange = {
                    startAt: timeRangeCount.range.startAt,
                    endAt: timeRangeCount.range.endAt,
                  };

                  const existingRanges = timeRangesByDayMapClone[day] || [];
                  const rangeKey = getTimeRangeKey(range);
                  if (existingRanges.some((range) => getTimeRangeKey(range) === rangeKey)) {
                    return;
                  }

                  timeRangesByDayMapClone[day] = [...existingRanges, range];
                });

                // Add days from timerange that do not have any count and set them to 0.
                let startAt = parseISO(timeRange.startAt);
                const endAt = parseISO(timeRange.endAt);
                // eslint-disable-next-line promise/always-return
                while (isBefore(startAt, endAt)) {
                  const dayStart = startOfDay(assertIsoTimestamp(startAt.toISOString()), timezone);
                  if (!countByDayMapClone[dayStart]) {
                    countByDayMapClone[dayStart] = 0;
                  }
                  startAt = addDays(parseISO(dayStart), 1);
                }

                onCountByDayMapChanged(cloneDeep(countByDayMapClone));
                onTimeRangesByDayMapChanged(cloneDeep(timeRangesByDayMapClone));
              })
              .catch((reason) => {
                onError(reason.message);
              })
          );
        });

      const limiter = getSuggestedTimesFetchLimiter(concurrencyLimitFromFlag(suggestedTimesFetchConcurrency));
      await Promise.all(tasks.map((task) => limiter.run(task)));
    },
    [
      taskId,
      applicationId,
      selfScheduleZoomHost,
      requestType,
      apolloClient,
      onEmployeeMissingZoomUserId,
      onError,
      suggestedTimesFetchConcurrency,
    ]
  );

  return loadSelfScheduleOptionsByTimeRange;
};

export default useLoadSelfScheduleOptionsByTimeRange;
